'use client';

import React, { useEffect, useState, useRef, useMemo } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { Search, ArrowRight, Puzzle, X, Waypoints } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMountShuffledList } from './shuffle-utils';

interface MissingStepMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; matches?: Record<string, string>; hints?: any[] };
  payload: {
    layout?: string;
    sequence: Array<{ id: string; content?: string; isMissing?: boolean; row?: number; col?: number; dependencies?: string[] }>;
    options: Array<{ id: string; content: string }>;
    _hasHints?: boolean;
  };
}

export default function MissingStepMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, payload }: any) {
  const actualMechanicId = mechanicId || id || '';
  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={prompt || ''}
      icon={<Search className="w-5 h-5" />}
      iconLabel="Missing Step — Fill in the blanks in the process"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
    >
      {(submissionState, setAnswer) => (
        <MissingStepMechanicInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} />
      )}
    </BaseMechanicShell>
  );
}


function MissingStepMechanicInner({ payload, submissionState, setAnswer }: any) {
  // assignments maps slotId (from sequence) -> optionId
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const sequence = useMemo(() => payload?.sequence || [], [payload?.sequence]);
  const options = useMountShuffledList<any>(payload?.options || []);
  const isBranch = payload?.layout === 'branch';

  const maxCols = isBranch ? Math.max(1, ...sequence.map((s: any) => (s.col || 0) + 1)) : 0;
  const maxRows = isBranch ? Math.max(1, ...sequence.map((s: any) => (s.row || 0) + 1)) : 0;

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        setDimensions({
          width: entries[0].contentRect.width,
          height: entries[0].contentRect.height
        });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isBranch]);

  // Helper to get coordinates for SVG lines
  const getCoords = (row: number, col: number) => {
    if (dimensions.width === 0) return { x: 0, y: 0 };
    return {
      x: ((col + 0.5) / maxCols) * dimensions.width,
      y: ((row + 0.5) / maxRows) * dimensions.height
    };
  };

  // Notify parent of answer changes
  useEffect(() => {
    const missingCount = sequence.filter((s: any) => s.isMissing).length;
    const assignedCount = Object.keys(assignments).length;

    if (assignedCount > 0 && assignedCount === missingCount) {
      setAnswer(assignments);
    } else {
      setAnswer(null); // Not fully answered
    }
  }, [assignments, sequence, setAnswer]);

  const handleOptionClick = (optionId: string) => {
    if (submissionState !== 'idle' && submissionState !== 'error') return;

    // Find the first empty slot
    const emptySlot = sequence.find((s: any) => s.isMissing && !assignments[s.id]);
    if (emptySlot) {
      setAssignments((prev) => ({ ...prev, [emptySlot.id]: optionId }));
    }
  };

  const handleSlotClick = (slotId: string) => {
    if (submissionState !== 'idle' && submissionState !== 'error') return;
    
    // Remove the assignment for this slot
    if (assignments[slotId]) {
      setAssignments((prev) => {
        const next = { ...prev };
        delete next[slotId];
        return next;
      });
    }
  };

  // Get options that are NOT currently assigned
  const assignedOptionIds = Object.values(assignments);
  const availableOptions = options.filter((opt: any) => !assignedOptionIds.includes(opt.id));

  return (
    <div className="flex flex-col gap-8 w-full select-none">
      
      {/* 1. The Pipeline / Branch View */}
      <div
        className={cn(
          "p-6 bg-background/50 border border-border/50 rounded-2xl shadow-inner relative",
          isBranch
            ? "overflow-hidden"
            : "overflow-x-auto overflow-y-hidden pb-4 [scrollbar-width:thin] [scrollbar-color:rgba(100,116,139,0.5)_transparent]"
        )}
      >
        <div 
          ref={containerRef}
          className={cn(
            isBranch
              ? "grid gap-5 relative min-h-[280px]"
              : "flex items-center gap-2 relative w-max min-w-full pr-2"
          )}
          style={isBranch ? { 
            gridTemplateColumns: `repeat(${maxCols}, minmax(140px, 1fr))`,
            gridTemplateRows: `repeat(${maxRows}, minmax(100px, 1fr))`
          } : undefined}
        >
          {/* Branch SVG Layer */}
          {isBranch && dimensions.width > 0 && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="25" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="currentColor" className="text-border" />
                </marker>
              </defs>
              {sequence.map((step: any) => {
                if (!step.dependencies) return null;
                return step.dependencies.map((depId: string) => {
                  const parent = sequence.find((s: any) => s.id === depId);
                  if (!parent) return null;
                  const start = getCoords(parent.row || 0, parent.col || 0);
                  const end = getCoords(step.row || 0, step.col || 0);
                  
                  // Dynamic curve based on flow direction
                  const isVertical = Math.abs(end.y - start.y) > Math.abs(end.x - start.x);
                  let cp1x, cp1y, cp2x, cp2y;
                  
                  if (isVertical) {
                    cp1x = start.x;
                    cp1y = start.y + (end.y - start.y) / 2;
                    cp2x = end.x;
                    cp2y = start.y + (end.y - start.y) / 2;
                  } else {
                    cp1x = start.x + (end.x - start.x) / 2;
                    cp1y = start.y;
                    cp2x = start.x + (end.x - start.x) / 2;
                    cp2y = end.y;
                  }

                  const pathD = `M ${start.x} ${start.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${end.x} ${end.y}`;

                  return (
                    <path 
                      key={`${parent.id}-${step.id}`}
                      d={pathD}
                      fill="none"
                      stroke="currentColor" 
                      className="text-border"
                      strokeWidth="2"
                      strokeDasharray="6 4"
                      markerEnd="url(#arrowhead)"
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                });
              })}
            </svg>
          )}

          {sequence.map((step: any, index: number) => {
            const isLast = index === sequence.length - 1;
            const isSlot = step.isMissing;
            const assignedOptionId = assignments[step.id];
            const assignedOption = options.find((o: any) => o.id === assignedOptionId);

            const gridStyle = isBranch ? { gridRow: (step.row || 0) + 1, gridColumn: (step.col || 0) + 1 } : undefined;

            return (
              <React.Fragment key={step.id}>
                <div style={gridStyle} className={cn("z-10 flex justify-center items-center h-full", isBranch ? "w-full" : "w-auto shrink-0")}>
                  {isSlot ? (
                    // Missing Step Slot
                    <div 
                      onClick={() => handleSlotClick(step.id)}
                      className={cn(
                        "relative flex flex-col items-center justify-center w-full min-w-[112px] max-w-[160px] min-h-[64px] p-3 rounded-lg border-2 transition-all duration-300 text-center",
                        assignedOption 
                          ? "bg-primary/10 border-primary text-primary-foreground shadow-[0_0_15px_rgba(var(--primary),0.2)] cursor-pointer" 
                          : "bg-background border-dashed border-muted-foreground/50 text-muted-foreground shadow-[0_0_15px_rgba(0,0,0,0.1)] animate-pulse"
                      )}
                    >
                      {assignedOption ? (
                        <>
                          <span className="text-xs font-medium leading-snug text-primary">{assignedOption.content}</span>
                          <div className="absolute -top-2 -right-2 bg-background border border-border rounded-full p-1 opacity-0 hover:opacity-100 transition-opacity">
                            <X className="w-3 h-3 text-muted-foreground" />
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center gap-1 opacity-50">
                          <Puzzle className="w-4 h-4" />
                          <span className="text-[10px] uppercase tracking-wider font-semibold">Missing Node</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Normal Step Node
                    <div className="flex flex-col items-center justify-center w-full min-w-[112px] max-w-[160px] min-h-[64px] p-3 bg-muted/90 backdrop-blur-sm border border-border/50 rounded-lg text-center shadow-md">
                      <span className="text-xs leading-snug text-foreground/90 font-medium">
                        {step.content}
                      </span>
                    </div>
                  )}
                </div>

                {!isLast && !isBranch && (
                  <div className="flex items-center text-muted-foreground/50 px-1.5">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        {!isBranch && (
          <div className="mt-3 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground/70">
            Scroll sideways to view the full flow
          </div>
        )}
      </div>

      {/* 2. The Data Pool (Options) */}
      <div className="flex flex-col gap-3">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1 flex items-center gap-2">
          <Waypoints className="w-4 h-4" />
          Available Data Fragments
        </h4>
        <div className="flex flex-wrap gap-2.5">
          {availableOptions.length === 0 ? (
            <div className="w-full p-4 border border-dashed border-border/50 rounded-xl text-center text-muted-foreground text-sm">
              All fragments routed. Submit your sequence.
            </div>
          ) : (
            availableOptions.map((opt: any) => (
              <button
                type="button"
                key={opt.id}
                onClick={() => handleOptionClick(opt.id)}
                disabled={submissionState !== 'idle' && submissionState !== 'error'}
                className="group relative flex items-center gap-2.5 px-4 py-2.5 bg-card border border-border hover:border-primary/50 hover:bg-primary/5 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="p-1.5 rounded-md bg-muted/50 group-hover:bg-primary/10 transition-colors">
                  <Puzzle className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <span className="text-xs font-medium leading-snug text-foreground/90">{opt.content}</span>
              </button>
            ))
          )}
        </div>
      </div>

    </div>
  );
}
